import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { format } from 'date-fns';

function Documents({ user }) {
  const [documents, setDocuments] = useState([]);
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [loading, setLoading] = useState(true);

  const [newDocument, setNewDocument] = useState({
    document_type: 'permis_conduire',
    document_name: '',
    expiry_date: '',
    notes: '',
    file: null
  });

  useEffect(() => {
    fetchDocuments();
  }, []);

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/documents', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDocuments(response.data);
    } catch (error) {
      console.error('Erreur documents:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    setNewDocument({ ...newDocument, file: e.target.files[0] });
  };

  const handleSubmitDocument = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('document_type', newDocument.document_type);
      formData.append('document_name', newDocument.document_name);
      formData.append('expiry_date', newDocument.expiry_date);
      formData.append('notes', newDocument.notes);
      if (newDocument.file) {
        formData.append('file', newDocument.file);
      }

      await axios.post('/api/documents', formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      alert('Document uploadé avec succès!');
      setShowUploadForm(false);
      setNewDocument({
        document_type: 'permis_conduire',
        document_name: '',
        expiry_date: '',
        notes: '',
        file: null
      });
      fetchDocuments();
    } catch (error) {
      console.error('Erreur upload:', error);
      alert('Erreur lors de l\'upload du document.');
    }
  };

  const handleVerifyDocument = async (docId, status, notes = '') => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`/api/documents/${docId}/verify`, {
        status,
        notes
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      alert(`Document ${status === 'approved' ? 'approuvé' : 'rejeté'}!`);
      setSelectedDocument(null);
      fetchDocuments();
    } catch (error) {
      console.error('Erreur vérification:', error);
      alert('Erreur lors de la vérification.');
    }
  };

  const handleDeleteDocument = async (docId) => {
    if (!window.confirm('Supprimer ce document ?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`/api/documents/${docId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      alert('Document supprimé!');
      setSelectedDocument(null);
      fetchDocuments();
    } catch (error) {
      console.error('Erreur suppression:', error);
      alert('Erreur lors de la suppression.');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: '#f39c12',
      approved: '#27ae60',
      rejected: '#e74c3c',
      expired: '#95a5a6'
    };
    return colors[status] || '#999';
  };

  const getStatusLabel = (status) => {
    const labels = {
      pending: '⏳ En attente',
      approved: '✅ Approuvé',
      rejected: '❌ Rejeté',
      expired: '⚠️ Expiré'
    };
    return labels[status] || status;
  };

  const getDocumentTypeLabel = (type) => {
    const labels = {
      permis_conduire: '🎫 Permis de conduire',
      carte_vitale: '💳 Carte Vitale',
      carte_identite: '🪪 Carte d\'identité',
      attestation_securite_sociale: '🏥 Attestation Sécurité Sociale',
      rib: '🏦 RIB',
      justificatif_domicile: '🏠 Justificatif de domicile',
      carte_grise: '📋 Carte grise',
      assurance_vehicule: '🛡️ Assurance véhicule',
      controle_technique: '🔧 Contrôle technique',
      fimo_fcos: '📜 FIMO / FCOS',
      carte_chronotachygraphe: '🎴 Carte chronotachygraphe',
      autre: '📄 Autre'
    };
    return labels[type] || type;
  };

  const isExpiringSoon = (expiryDate) => {
    if (!expiryDate) return false;
    const days = Math.ceil((new Date(expiryDate) - new Date()) / (1000 * 60 * 60 * 24));
    return days <= 30 && days > 0;
  };

  const isExpired = (expiryDate) => {
    if (!expiryDate) return false;
    return new Date(expiryDate) < new Date();
  };

  if (loading) {
    return (
      <div className="container">
        <div className="loading">Chargement des documents...</div>
      </div>
    );
  }

  return (
    <div className="container">
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
          <h2 style={{ margin: 0 }}>📄 Mes Documents</h2>
          {user.role === 'driver' && (
            <button
              onClick={() => {
                setShowUploadForm(true);
                setSelectedDocument(null);
              }}
              style={{
                padding: '0.7rem 1.5rem',
                background: '#B8472E',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: 'bold'
              }}
            >
              📤 Uploader un document
            </button>
          )}
        </div>

        {/* Alertes documents expirants */}
        {documents.some(doc => isExpiringSoon(doc.expiry_date) || isExpired(doc.expiry_date)) && (
          <div style={{
            background: '#fff3cd',
            border: '2px solid #B9831C',
            borderRadius: '8px',
            padding: '1rem',
            marginBottom: '2rem'
          }}>
            <h4 style={{ margin: '0 0 0.5rem 0', color: '#856404' }}>⚠️ Documents nécessitant votre attention:</h4>
            <ul style={{ margin: 0, paddingLeft: '1.5rem' }}>
              {documents.filter(doc => isExpiringSoon(doc.expiry_date)).map(doc => (
                <li key={doc.id} style={{ color: '#856404' }}>
                  {getDocumentTypeLabel(doc.document_type)} expire le {format(new Date(doc.expiry_date), 'dd/MM/yyyy')}
                </li>
              ))}
              {documents.filter(doc => isExpired(doc.expiry_date)).map(doc => (
                <li key={doc.id} style={{ color: '#c62828', fontWeight: 'bold' }}>
                  {getDocumentTypeLabel(doc.document_type)} est expiré depuis le {format(new Date(doc.expiry_date), 'dd/MM/yyyy')}
                </li>
              ))}
            </ul>
          </div>
        )}

        {showUploadForm && (
          <div style={{ background: '#f8f9fa', padding: '2rem', borderRadius: '12px', marginBottom: '2rem' }}>
            <h3 style={{ marginBottom: '1.5rem' }}>Uploader un nouveau document</h3>
            <form onSubmit={handleSubmitDocument}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Type de document</label>
                  <select
                    value={newDocument.document_type}
                    onChange={(e) => setNewDocument({ ...newDocument, document_type: e.target.value })}
                    required
                    style={{ width: '100%', padding: '0.7rem', borderRadius: '6px', border: '1px solid #ddd' }}
                  >
                    <option value="permis_conduire">🎫 Permis de conduire</option>
                    <option value="carte_vitale">💳 Carte Vitale</option>
                    <option value="carte_identite">🪪 Carte d'identité</option>
                    <option value="attestation_securite_sociale">🏥 Attestation Sécurité Sociale</option>
                    <option value="rib">🏦 RIB</option>
                    <option value="justificatif_domicile">🏠 Justificatif de domicile</option>
                    <option value="carte_grise">📋 Carte grise</option>
                    <option value="assurance_vehicule">🛡️ Assurance véhicule</option>
                    <option value="controle_technique">🔧 Contrôle technique</option>
                    <option value="fimo_fcos">📜 FIMO / FCOS</option>
                    <option value="carte_chronotachygraphe">🎴 Carte chronotachygraphe</option>
                    <option value="autre">📄 Autre</option>
                  </select>
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Nom du document</label>
                  <input
                    type="text"
                    value={newDocument.document_name}
                    onChange={(e) => setNewDocument({ ...newDocument, document_name: e.target.value })}
                    required
                    placeholder="Ex: Permis_Dupont_2025"
                    style={{ width: '100%', padding: '0.7rem', borderRadius: '6px', border: '1px solid #ddd' }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Date d'expiration</label>
                  <input
                    type="date"
                    value={newDocument.expiry_date}
                    onChange={(e) => setNewDocument({ ...newDocument, expiry_date: e.target.value })}
                    style={{ width: '100%', padding: '0.7rem', borderRadius: '6px', border: '1px solid #ddd' }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Fichier (PDF, JPG, PNG)</label>
                  <input
                    type="file"
                    onChange={handleFileChange}
                    accept=".pdf,.jpg,.jpeg,.png"
                    style={{ width: '100%', padding: '0.7rem', borderRadius: '6px', border: '1px solid #ddd' }}
                  />
                </div>
              </div>

              <div style={{ marginTop: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Notes (optionnel)</label>
                <textarea
                  value={newDocument.notes}
                  onChange={(e) => setNewDocument({ ...newDocument, notes: e.target.value })}
                  rows={2}
                  style={{
                    width: '100%',
                    padding: '0.7rem',
                    borderRadius: '6px',
                    border: '1px solid #ddd',
                    fontFamily: 'inherit'
                  }}
                />
              </div>

              <div style={{ display: 'flex', gap: '1rem', marginTop: '1.5rem' }}>
                <button
                  type="submit"
                  style={{
                    padding: '0.7rem 1.5rem',
                    background: '#27ae60',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: 'bold'
                  }}
                >
                  📤 Uploader
                </button>
                <button
                  type="button"
                  onClick={() => setShowUploadForm(false)}
                  style={{
                    padding: '0.7rem 1.5rem',
                    background: '#95a5a6',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer'
                  }}
                >
                  Annuler
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Liste des documents */}
        {documents.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '4rem', color: '#999' }}>
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📄</div>
            <p>Aucun document uploadé</p>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '1.5rem' }}>
            {documents.map(doc => (
              <div
                key={doc.id}
                onClick={() => setSelectedDocument(doc)}
                style={{
                  padding: '1.5rem',
                  background: 'white',
                  border: `2px solid ${getStatusColor(doc.status)}`,
                  borderRadius: '12px',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)'}
                onMouseLeave={(e) => e.currentTarget.style.boxShadow = 'none'}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
                  <span style={{
                    padding: '0.4rem 1rem',
                    background: getStatusColor(doc.status),
                    color: 'white',
                    borderRadius: '20px',
                    fontSize: '0.85rem',
                    fontWeight: 'bold'
                  }}>
                    {getStatusLabel(doc.status)}
                  </span>
                  {isExpiringSoon(doc.expiry_date) && (
                    <span style={{ fontSize: '1.5rem' }}>⚠️</span>
                  )}
                  {isExpired(doc.expiry_date) && (
                    <span style={{ fontSize: '1.5rem' }}>🚫</span>
                  )}
                </div>

                <h3 style={{ fontSize: '1.1rem', marginBottom: '0.5rem' }}>
                  {getDocumentTypeLabel(doc.document_type)}
                </h3>

                <div style={{ fontSize: '0.9rem', color: '#666', marginBottom: '0.5rem' }}>
                  📝 {doc.document_name}
                </div>

                {doc.driver_name && (
                  <div style={{ fontSize: '0.9rem', color: '#666', marginBottom: '0.5rem' }}>
                    👤 {doc.driver_name}
                  </div>
                )}

                {doc.expiry_date && (
                  <div style={{
                    fontSize: '0.9rem',
                    color: isExpired(doc.expiry_date) ? '#c62828' : (isExpiringSoon(doc.expiry_date) ? '#f57c00' : '#666'),
                    fontWeight: (isExpired(doc.expiry_date) || isExpiringSoon(doc.expiry_date)) ? 'bold' : 'normal',
                    marginBottom: '0.5rem'
                  }}>
                    📅 Expire le: {format(new Date(doc.expiry_date), 'dd/MM/yyyy')}
                  </div>
                )}

                <div style={{ fontSize: '0.85rem', color: '#999' }}>
                  Uploadé: {format(new Date(doc.uploaded_at), 'dd/MM/yyyy')}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Modal détails document */}
        {selectedDocument && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0,0,0,0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000
            }}
            onClick={() => setSelectedDocument(null)}
          >
            <div
              style={{
                background: 'white',
                padding: '2rem',
                borderRadius: '12px',
                maxWidth: '600px',
                width: '90%',
                maxHeight: '80vh',
                overflowY: 'auto'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <h2 style={{ marginBottom: '1.5rem' }}>Détails du document</h2>

              <div style={{ marginBottom: '1rem' }}>
                <strong>Type:</strong> {getDocumentTypeLabel(selectedDocument.document_type)}
              </div>
              <div style={{ marginBottom: '1rem' }}>
                <strong>Nom:</strong> {selectedDocument.document_name}
              </div>
              <div style={{ marginBottom: '1rem' }}>
                <strong>Statut:</strong> {getStatusLabel(selectedDocument.status)}
              </div>
              {selectedDocument.driver_name && (
                <div style={{ marginBottom: '1rem' }}>
                  <strong>Chauffeur:</strong> {selectedDocument.driver_name}
                </div>
              )}
              {selectedDocument.expiry_date && (
                <div style={{ marginBottom: '1rem' }}>
                  <strong>Date d'expiration:</strong> {format(new Date(selectedDocument.expiry_date), 'dd/MM/yyyy')}
                </div>
              )}
              <div style={{ marginBottom: '1rem' }}>
                <strong>Uploadé le:</strong> {format(new Date(selectedDocument.uploaded_at), 'dd/MM/yyyy à HH:mm')}
              </div>
              {selectedDocument.verified_at && (
                <div style={{ marginBottom: '1rem' }}>
                  <strong>Vérifié le:</strong> {format(new Date(selectedDocument.verified_at), 'dd/MM/yyyy à HH:mm')}
                </div>
              )}
              {selectedDocument.notes && (
                <div style={{ marginBottom: '1rem' }}>
                  <strong>Notes:</strong>
                  <p style={{ background: '#f8f9fa', padding: '1rem', borderRadius: '6px', marginTop: '0.5rem' }}>
                    {selectedDocument.notes}
                  </p>
                </div>
              )}

              {selectedDocument.file_path && (
                <div style={{ marginTop: '1.5rem' }}>
                  <a
                    href={`/api/documents/${selectedDocument.id}/download`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: 'inline-block',
                      padding: '0.7rem 1.5rem',
                      background: '#B8472E',
                      color: 'white',
                      textDecoration: 'none',
                      borderRadius: '8px',
                      fontWeight: 'bold'
                    }}
                  >
                    📥 Télécharger le fichier
                  </a>
                </div>
              )}

              {/* Actions admin */}
              {(user.role === 'admin' || user.role === 'manager') && selectedDocument.status === 'pending' && (
                <div style={{ marginTop: '2rem', borderTop: '2px solid #e0e0e0', paddingTop: '1.5rem' }}>
                  <h3 style={{ marginBottom: '1rem' }}>Actions (Admin)</h3>
                  <div style={{ display: 'flex', gap: '1rem' }}>
                    <button
                      onClick={() => handleVerifyDocument(selectedDocument.id, 'approved')}
                      style={{
                        padding: '0.7rem 1.5rem',
                        background: '#27ae60',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontWeight: 'bold'
                      }}
                    >
                      ✅ Approuver
                    </button>
                    <button
                      onClick={() => {
                        const notes = prompt('Raison du rejet:');
                        if (notes) handleVerifyDocument(selectedDocument.id, 'rejected', notes);
                      }}
                      style={{
                        padding: '0.7rem 1.5rem',
                        background: '#e74c3c',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontWeight: 'bold'
                      }}
                    >
                      ❌ Rejeter
                    </button>
                  </div>
                </div>
              )}

              {/* Bouton supprimer */}
              {(user.role === 'driver' || user.role === 'admin') && (
                <button
                  onClick={() => handleDeleteDocument(selectedDocument.id)}
                  style={{
                    marginTop: '1rem',
                    padding: '0.7rem 1.5rem',
                    background: '#e74c3c',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    width: '100%'
                  }}
                >
                  🗑️ Supprimer
                </button>
              )}

              <button
                onClick={() => setSelectedDocument(null)}
                style={{
                  marginTop: '1rem',
                  padding: '0.7rem 1.5rem',
                  background: '#95a5a6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  width: '100%'
                }}
              >
                Fermer
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Documents;
